<template>
  <div
    ref="element"
    class="tooltip absolute -translate-x-1/2 -translate-y-1/2 text-white"
    :class="{
      'z-10': isOpen,
    }"
    :style="{
      left: `${data.position_x}%`,
      top: `${data.position_y}%`,
    }"
  >
    <!-- BADGE -->
    <button
      type="button"
      class="
        group relative flex size-5 items-center justify-center rounded-full bg-blue-500 transition-all
        ease-in-out will-change-transform typo-body-lg
        lg:size-7 lg:typo-body-xl
      "
      :class="{
        'hover:scale-125 hover:bg-blue-600': !isOpen,
      }"
      :aria-describedby="`tooltip-${index}`"
      :aria-expanded="isOpen"
      @click="toggle()"
    >
      <!-- <span
        aria-hidden="true"
        class="absolute inset-0 rounded-full bg-blue-500 opacity-75"
        :class="{ 'animate-[ping_0.5s_ease-in-out]': isOpen }"
      /> -->
      <span class="relative">{{ index + 1 }}</span>
    </button>

    <!-- ARROW -->
    <div
      aria-hidden="true"
      class="tooltip-arrow absolute left-1/2 top-1/2 size-[1.1875rem] rotate-45 bg-blue-500 opacity-0"
      :class="tooltipArrowPositionStyles"
    />

    <!-- CONTENT -->
    <div
      :id="`tooltip-${index}`"
      role="tooltip"
      class="tooltip-content absolute h-0 overflow-hidden bg-blue-500 opacity-0"
      :class="tooltipPositionStyles"
    >
      <div class="px-6 pb-5 pt-6">
        <!-- Close Button -->
        <button
          :aria-label="$t('aria_labels.tooltip_close')"
          class="slide-up absolute right-1 top-1 transition-colors hover:text-blue-200"
          type="button"
          @click="hide()"
        >
          <IconPlus class="w-7 rotate-45 transition-transform group-hover:rotate-[135deg]" />
        </button>

        <!-- Title -->
        <h3 class="slide-up mb-3 flex shrink-0 gap-3">
          <span class="flex size-7 items-center justify-center rounded-full bg-white !font-semibold text-blue-500"
          >
            {{ index + 1 }}
          </span>

          <span class="!max-w-[45ch] !font-semibold typo-body-2xl" v-html="data.heading" />
        </h3>

        <!-- Text -->
        <div class="slide-up link w-max !max-w-[35vw] typo-body-lg 3xl:!max-w-[60ch]" v-html="data.text" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap'
import type { ComponentsMainInteractiveDocumentFragment } from '#gql'

const props = defineProps<{
  index: number
  data: Enforce<NonNullable<ComponentsMainInteractiveDocumentFragment['tooltips']>[number]>
}>()

const isOpen = ref<boolean>(false)
const element = ref<HTMLElement | null>(null)

// Computed if tooltip relative position is top, left, right, bottom
const tooltipPosition = computed(() => {
  const positionMap: Record<string, string> = {
    top_left: 'top',
    top_center: 'top',
    top_right: 'top',
    bottom_left: 'bottom',
    bottom_center: 'bottom',
    bottom_right: 'bottom',
    left: 'left',
    right: 'right',
  }

  const position = props.data?.position_tooltip?.value || 'top_center'
  return positionMap[position] || 'top'
})

// Computed styles for tooltip position
const tooltipPositionStyles = computed(() => {
  const positionStyles: {
    [key: string]: {
      base: string
      transform: string
      rounded: string
    }
  } = {
    top_left: {
      base: 'top-0 left-0',
      transform: 'translate-x-[calc(-100%+1.75rem)] translate-y-[calc(-100%-1rem)]',
      rounded: 'rounded-bl-[1.25rem]',
    },
    top_center: {
      base: 'top-0 left-1/2',
      transform: 'translate-x-[-50%] translate-y-[calc(-100%-1rem)]',
      rounded: 'rounded-br-[1.25rem]',
    },
    top_right: {
      base: 'top-0 right-0',
      transform: 'translate-x-[calc(100%-1.75rem)] translate-y-[calc(-100%-1rem)]',
      rounded: 'rounded-br-[1.25rem]',
    },
    bottom_left: {
      base: 'bottom-0 left-0',
      transform: 'translate-x-[calc(-100%+1.75rem)] translate-y-[calc(100%+1rem)]',
      rounded: 'rounded-br-[1.25rem]',
    },
    bottom_center: {
      base: 'bottom-0 left-1/2',
      transform: 'translate-x-[-50%] translate-y-[calc(100%+1rem)]',
      rounded: 'rounded-br-[1.25rem]',
    },
    bottom_right: {
      base: 'bottom-0 right-0',
      transform: 'translate-x-[calc(100%-1.75rem)] translate-y-[calc(100%+1rem)]',
      rounded: 'rounded-br-[1.25rem]',
    },
    right: {
      base: 'right-0 top-1/2',
      transform: 'translate-x-[calc(100%+1rem)] translate-y-[-50%]',
      rounded: 'rounded-br-[1.25rem]',
    },
    left: {
      base: 'left-0 top-1/2',
      transform: 'translate-x-[calc(-100%-1rem)] translate-y-[-50%]',
      rounded: 'rounded-bl-[1.25rem]',
    },
  }

  let position = props.data?.position_tooltip?.value || 'top_center'

  if (position in positionStyles === false) {
    position = 'top_center'
  }

  const styles = positionStyles[position]

  // Combine the classes
  return [styles.base, styles.transform, styles.rounded].join(' ')
})

// Computed styles for tooltip arrow position
const tooltipArrowPositionStyles = computed(() => {
  const position = tooltipPosition.value

  const styleMap: Record<string, string> = {
    top: '-translate-x-1/2 translate-y-[calc(-50%-1.85rem-0.85rem)]',
    bottom: '-translate-x-1/2 translate-y-[calc(-50%+1.85rem+0.85rem)]',
    left: 'translate-x-[calc(-50%-1.85rem-0.85rem)] -translate-y-1/2',
    right: 'translate-x-[calc(-50%+1.85rem+0.85rem)] -translate-y-1/2',
  }

  return styleMap[position] || ''
})

async function toggle() {
  if (!isOpen.value) {
    show()
  } else {
    hide()
  }
}

let tl: gsap.core.Timeline | null = null
async function show() {
  if (tl?.isActive()) return // Skip if timeline is active

  isOpen.value = true

  const content = element.value?.querySelector('.tooltip-content') // Select tooltip content element
  const arrow = element.value?.querySelector('.tooltip-arrow') // Select the arrow element
  const slideUpItems = element.value?.querySelectorAll('.slide-up') // Select all .slide-up elements

  // Create or reset the timeline
  tl = gsap.timeline({
    defaults: {
      duration: 0.6,
      ease: 'circ.inOut',
    },
  })

  tl
    .to(content, {
      autoAlpha: 1,
      duration: 0.3,
      height: 'auto',
    })
    .to(slideUpItems, {
      autoAlpha: 1,
      translateY: 0,
      duration: 0.3,
      stagger: 0.1,
    }, '-=0.1')
    .to(arrow, {
      autoAlpha: 1,
      duration: 0.1,
    }, '<')
    .to(arrow, {
      y: () => (tooltipPosition.value === 'top' ? '+=0.85rem' : tooltipPosition.value === 'bottom' ? '-=0.85rem' : '+=0'),
      x: () => (tooltipPosition.value === 'left' ? '+=0.85rem' : tooltipPosition.value === 'right' ? '-=0.85rem' : '+=0'),
      duration: 0.1,
    })

  await tl
}

async function hide() {
  if (!tl) {
    return
  }

  // Wait for the timeline to finish if it's active
  if (tl.isActive()) {
    await new Promise((resolve) => {
      const checkActive = () => {
        if (!tl.isActive()) {
          resolve()
        } else {
          setTimeout(checkActive, 50) // Check every 50ms
        }
      }
      checkActive()
    })
  }

  // Reverse the timeline
  await tl.reverse()

  isOpen.value = false
}

onUnmounted(() => {
  tl?.kill() // Kill the timeline
})
</script>

<style>
.tooltip {
  .slide-up {
    @apply invisible;
    transform: translateY(1.5rem);
  }
}
</style>
